"use client";

import { Globe, Check } from "lucide-react";
import { useLocale } from "next-intl";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState, useTransition } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Cookies from "js-cookie";

type Locale = "en" | "ar";

const LANGUAGES: { code: Locale; label: string }[] = [
  { code: "en", label: "English" },
  { code: "ar", label: "العربية" },
];

export default function LanguageSwitcher({
  isScrolled,
}: {
  isScrolled?: boolean;
}) {
  const locale = useLocale() as Locale;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();

  const [open, setOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  const ref = useRef<HTMLDivElement>(null);

  const isRTL = locale === "ar";

  /* Close on outside click */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const switchLocale = (nextLocale: Locale) => {
    if (nextLocale === locale) return;

    Cookies.set("LOGIC_HUB_LOCALE", nextLocale, {
      path: "/",
      expires: 30,
    });

    const segments = pathname.split("/");
    segments[1] = nextLocale;

    const newPath =
      segments.join("/") +
      (searchParams.toString() ? `?${searchParams.toString()}` : "");

    startTransition(() => {
      router.replace(newPath);
    });

    setOpen(false);
  };

  return (
    <div ref={ref} className="relative select-none">
      {/* Trigger */}
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        disabled={isPending}
        className={`flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-4 py-2  backdrop-blur-md transition hover:bg-white/20 disabled:opacity-50 text-white`}
      >
        <Globe className="h-5 w-5" />
        <span className="text-sm font-medium">
          {locale === "ar" ? "العربية" : "English"}
        </span>
      </button>

      {/* Menu */}
      <AnimatePresence>
        {open && (
          <motion.ul
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className={`absolute top-full z-50 mt-2 w-40 overflow-hidden rounded-2xl border bg-white/90 shadow-lg backdrop-blur-xl
              ${isRTL ? "right-0 text-right" : "left-0 text-left"}`}
            role="menu"
          >
            {LANGUAGES.map((lang) => {
              const active = locale === lang.code;

              return (
                <li
                  key={lang.code}
                  role="menuitem"
                  onClick={() => switchLocale(lang.code)}
                  className={`flex cursor-pointer items-center justify-between px-4 py-3 text-sm font-medium transition
                    ${
                      active
                        ? "bg-primary/10 text-primary"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                >
                  <span>{lang.label}</span>

                  {active && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="text-primary"
                    >
                      <Check className="h-4 w-4" />
                    </motion.span>
                  )}
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
